import { createContext, useState, useEffect } from "react";
import { Amplify } from "aws-amplify";
import {
  signIn,
  confirmSignIn,
  signOut as amplifySignOut,
  fetchAuthSession,
  getCurrentUser,
} from "aws-amplify/auth";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { sessionStorage as amplifySessionStorage } from "aws-amplify/utils";
import { useHistory } from "react-router-dom";

const AuthContext = createContext();
export default AuthContext;

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_USER_POOL_ID,
      userPoolClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID,
      ...(process.env.REACT_APP_IDENTITY_POOL_ID
        ? { identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID }
        : {}),
    },
  },
});

// Let Amplify own token storage/refresh, backed by sessionStorage (tab-scoped).
// The refresh token is held internally by Amplify and never exposed to app code.
cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifySessionStorage);

export const AuthProvider = ({ children }) => {
  // `user` holds the decoded ID-token claims (Amplify manages the raw tokens).
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [totpSecretCode, setTotpSecretCode] = useState(""); // otpauth:// URI for the QR
  // Retained across chained challenge steps (e.g. new password -> TOTP setup).
  const [currentUsername, setCurrentUsername] = useState("");

  const history = useHistory();

  const resetChallengeState = () => {
    setNewPasswordRequired(false);
    setMfaRequired(false);
    setMfaSetupRequired(false);
  };

  // Populate `user` from the current ID-token claims managed by Amplify.
  const hydrateUser = async () => {
    const session = await fetchAuthSession();
    const claims = session.tokens?.idToken?.payload ?? null;
    setUser(claims);
    return claims;
  };

  // Route a v6 sign-in `nextStep` to the appropriate UI state.
  const handleNextStep = async (nextStep, accountName) => {
    switch (nextStep.signInStep) {
      case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
        resetChallengeState();
        setNewPasswordRequired(true);
        break;
      case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP": {
        const uri = nextStep.totpSetupDetails.getSetupUri("ME-SCT", accountName);
        resetChallengeState();
        setTotpSecretCode(uri.toString());
        setMfaSetupRequired(true);
        break;
      }
      case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        resetChallengeState();
        setMfaRequired(true);
        break;
      case "DONE":
        resetChallengeState();
        await hydrateUser();
        history.push("/");
        break;
      default:
        console.warn("Unhandled sign-in step:", nextStep.signInStep);
    }
  };

  const loginUser = async (username, password) => {
    try {
      const { nextStep } = await signIn({ username, password });
      setCurrentUsername(username);
      await handleNextStep(nextStep, username);
    } catch (error) {
      console.error("Login failed:", error?.name);
      alert("Wrong username or password!");
    }
  };

  const completeNewPassword = async (newPassword) => {
    try {
      const { nextStep } = await confirmSignIn({ challengeResponse: newPassword });
      await handleNextStep(nextStep, currentUsername);
    } catch (error) {
      console.error("Failed to set new password:", error?.name);
      alert("Failed to set new password. Please try again.");
    }
  };

  const confirmMfaEnrollment = async (code) => {
    try {
      const { nextStep } = await confirmSignIn({ challengeResponse: code });
      await handleNextStep(nextStep, currentUsername);
    } catch (error) {
      console.error("MFA enrollment failed:", error?.name);
      alert("Failed to verify TOTP code.");
    }
  };

  const verifyMfaCode = async () => {
    try {
      const { nextStep } = await confirmSignIn({ challengeResponse: mfaCode });
      await handleNextStep(nextStep, currentUsername);
    } catch (error) {
      console.error("MFA verification failed:", error?.name);
      alert("Invalid MFA code. Please try again.");
    }
  };

  const logoutUser = async () => {
    try {
      await amplifySignOut();
    } catch (error) {
      console.error("Error signing out:", error?.name);
    } finally {
      setUser(null);
      resetChallengeState();
      history.push("/login");
    }
  };

  // Kept as a named export for consumers; identical behaviour to logoutUser.
  const signOut = logoutUser;

  useEffect(() => {
    const bootstrap = async () => {
      try {
        await getCurrentUser(); // throws if there is no signed-in user
        await hydrateUser();
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  const contextData = {
    user,
    setUser,
    loginUser,
    logoutUser,
    signOut,
    mfaRequired,
    mfaCode,
    setMfaCode,
    verifyMfaCode,
    newPasswordRequired,
    completeNewPassword,
    mfaSetupRequired,
    totpSecretCode,
    confirmMfaEnrollment,
  };

  return (
    <AuthContext.Provider value={contextData}>
      {loading ? null : children}
    </AuthContext.Provider>
  );
};
